import { Link } from "react-router-dom";
import { Zap, Target, Heart, Users, ArrowRight, Award } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead } from "@/components/SEOHead";

const VALUES = [
  {
    icon: <Zap size={22} />,
    title: "Geschwindigkeit",
    desc: "Wir liefern was wir versprechen — in der Zeit die wir nennen. Demo in 24h, live in 48h. Kein Warten, kein Vertrösten.",
  },
  {
    icon: <Target size={22} />,
    title: "Resultate",
    desc: "Schöne Websites sind gut. Websites die Kunden bringen sind besser. Alles was wir tun hat einen klaren Zweck.",
  },
  {
    icon: <Heart size={22} />,
    title: "Fairness",
    desc: "Transparente Preise. Keine Fallstricke. Monatlich kündbar. Wir verdienen an zufriedenen Kunden — nicht an Verträgen.",
  },
  {
    icon: <Users size={22} />,
    title: "Partnerschaft",
    desc: "Wir denken langfristig. Ihr Erfolg ist unser Erfolg. Deshalb begleiten wir Sie auch nach dem Launch.",
  },
];

const TEAM = [
  {
    name: "Max Reinhardt",
    role: "Gründer & CEO",
    bio: "10 Jahre Erfahrung im Web-Business. Vorher Head of Digital bei einer Münchner Agentur. Heute Fokus auf lokale Unternehmen.",
    initials: "MR",
    color: "bg-cyan-400/10 text-cyan-400",
  },
  {
    name: "Julia Fischer",
    role: "Head of Design",
    bio: "UI/UX-Designerin mit Leidenschaft für saubere, konversionstarke Websites. Findet für jede Branche die perfekte Optik.",
    initials: "JF",
    color: "bg-purple-400/10 text-purple-400",
  },
  {
    name: "David Chen",
    role: "Lead Developer",
    bio: "Full-Stack-Entwickler und SEO-Spezialist. Sorgt dafür dass Websites nicht nur schön aussehen sondern auch gefunden werden.",
    initials: "DC",
    color: "bg-blue-400/10 text-blue-400",
  },
  {
    name: "Anna Weber",
    role: "Customer Success",
    bio: "Erste Ansprechpartnerin für unsere Kunden. Sorgt dafür dass jeder nach dem Launch bestens betreut ist.",
    initials: "AW",
    color: "bg-green-400/10 text-green-400",
  },
];

export default function UeberUnsPage() {
  return (
    <PublicLayout>
      <SEOHead
        title="Über uns"
        description="signalSolution — Wir sind eine junge Agentur mit Leidenschaft für digitales Wachstum. Schnell, ehrlich, resultatorientiert. Lernen Sie uns kennen."
        canonical="/ueber-uns"
      />
      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Über signalSolution</p>
            <h1
              className="font-black text-5xl sm:text-6xl lg:text-7xl text-white mb-6 leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Wir machen{" "}
              <span className="text-cyan-400">lokale Unternehmen</span>{" "}
              digital stark.
            </h1>
            <p className="text-xl text-white/50 leading-relaxed max-w-3xl">
              signalSolution ist eine spezialisierte Web-Agentur für lokale Unternehmen. Unser Ziel ist es, dass jeder Handwerker, Friseur, Physio und Gastronom eine professionelle Online-Präsenz hat — ohne den Stress und die Kosten traditioneller Agenturen.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-4">Unsere Geschichte</p>
            <h2
              className="font-black text-4xl text-white mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Warum signalSolution?
            </h2>
            <div className="space-y-4 text-white/60 text-sm leading-relaxed">
              <p>
                Wir haben selbst erlebt, wie Inhaber lokaler Unternehmen bei der Frage "Ich brauche eine neue Website" oft jahrelang warten. Zu teuer, zu kompliziert, zu langsam.
              </p>
              <p>
                Dabei ist eine professionelle Website heute das Wichtigste was ein lokales Unternehmen haben kann — mehr als Visitenkarten, Flyer oder Radiowerbung zusammen.
              </p>
              <p>
                signalSolution wurde gegründet um das zu ändern. Wir kombinieren modernste Technologie mit echtem Vertriebsverständnis: Demo zuerst, Entscheidung danach.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { num: "48h", label: "Bis zur Live-Website", color: "bg-cyan-400/10 border-cyan-400/20" },
              { num: "100+", label: "Kunden betreut", color: "bg-blue-400/10 border-blue-400/20" },
              { num: "98%", label: "Weiterempfehlung", color: "bg-green-400/10 border-green-400/20" },
              { num: "3", label: "Pakete auf Anfrage", color: "bg-purple-400/10 border-purple-400/20" },
            ].map((s) => (
              <div
                key={s.label}
                className={`p-6 rounded-2xl border ${s.color} text-center`}
              >
                <div
                  className="font-black text-4xl text-white mb-1"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {s.num}
                </div>
                <div className="text-xs text-white/40">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 bg-[#070d1a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-cyan-400 text-sm font-semibold uppercase tracking-widest mb-3">Was uns antreibt</p>
            <h2
              className="font-black text-4xl text-white"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Unsere Werte
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-[#0A1220] border border-white/5 rounded-2xl p-6 hover:border-cyan-400/20 transition-all group">
                <div className="w-11 h-11 rounded-xl bg-cyan-400/10 flex items-center justify-center text-cyan-400 mb-4 group-hover:bg-cyan-400/15 transition-colors">
                  {v.icon}
                </div>
                <h3 className="font-bold text-white mb-2">{v.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team-Sektion vorerst entfernt – wird später wieder eingefügt */}

      {/* CTA */}
      <section className="py-20 px-6 bg-[#070d1a]">
        <div className="max-w-2xl mx-auto text-center">
          <Award size={36} className="text-cyan-400 mx-auto mb-5" />
          <h2
            className="font-black text-4xl text-white mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Werden Sie unser nächster Erfolg.
          </h2>
          <p className="text-white/50 mb-8 text-lg">
            Gemeinsam bringen wir Ihr Unternehmen auf die erste Seite.
          </p>
          <Link
            to="/kontakt#demo-form"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/20"
          >
            Kontakt aufnehmen <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
